// The Rollout tab's history: Remote Config versions that changed
// `voice_engine`, newest first, each with "Edit as draft" and "Restore".

import type { HistoryEntry, PolicyHistory, TemplateVersion } from "../../services/voiceEngines";
import { formatDateTime } from "../analytics/format";
import { policySummary } from "./policy";

const PUBLISHER: Record<TemplateVersion["publisher"], { text: string; cls: string }> = {
  studio: { text: "Studio", cls: "ve-ok" },
  break_glass: { text: "break-glass", cls: "ve-warn" },
  external: { text: "console", cls: "ve-warn" },
};

export function PublisherBadge({ version }: { version: TemplateVersion }) {
  const p = PUBLISHER[version.publisher];
  return <span className={`badge ${p.cls}`}>{p.text}</span>;
}

export function HistoryPanel({
  history,
  error,
  canWrite,
  onEdit,
  onRestore,
}: {
  history: PolicyHistory | null;
  error: string | null;
  canWrite: boolean;
  onEdit: (entry: HistoryEntry) => void;
  onRestore: (entry: HistoryEntry) => void;
}) {
  return (
    <section className="panel ve-section" aria-labelledby="ve-hist-h">
      <h2 id="ve-hist-h" className="panel-head">
        History of voice_engine
      </h2>
      {error ? (
        <p className="error-text">History unavailable ({error}).</p>
      ) : !history ? (
        <p className="muted ve-small">Loading…</p>
      ) : history.entries.length === 0 ? (
        <p className="muted ve-small">No change to voice_engine in the last {history.scanned} template versions.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="guide-table" aria-label="Published versions">
              <thead>
                <tr>
                  <th className="no-sort">Template</th>
                  <th className="no-sort">By</th>
                  <th className="no-sort">Note</th>
                  <th className="no-sort">Value</th>
                  <th className="no-sort">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.entries.map((h) => (
                  <tr key={h.version.number ?? h.raw ?? ""}>
                    <td>
                      <div className="ve-row">
                        <span style={{ fontWeight: 600 }}>v{h.version.number}</span>
                        {h.live && <span className="badge ve-ok">live</span>}
                        {h.isV1 && <span className="badge">v1 value</span>}
                      </div>
                    </td>
                    <td>
                      <div className="ve-row">
                        <PublisherBadge version={h.version} />
                        <span>{h.version.by ?? "—"}</span>
                      </div>
                      <div className="muted ve-small">{formatDateTime(h.version.updateTime)}</div>
                    </td>
                    <td className="ve-small">{h.version.note || "—"}</td>
                    <td className="ve-small">
                      <span className={h.valid ? "" : "tt-weak-text"}>{policySummary(h.policy, h.valid)}</span>
                      {h.policy.experiment && (
                        <div className="muted mono">{h.policy.experiment}</div>
                      )}
                    </td>
                    <td>
                      {!h.live && (
                        <div className="row-actions">
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={!h.valid}
                            title={h.valid ? "Load this value into the draft above" : "An invalid value cannot be edited"}
                            onClick={() => onEdit(h)}
                          >
                            Edit as draft
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={!canWrite || !h.valid}
                            title="Publish this exact value again, as a new version"
                            onClick={() => onRestore(h)}
                          >
                            Restore
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!history.complete && (
            <p className="muted ve-small">
              Older changes are not shown: {history.scanned} template versions were looked at.
            </p>
          )}
        </>
      )}
    </section>
  );
}
